import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronLeft, ChevronRight, X, ArrowRight } from 'lucide-react';

/**
 * The analyst's follow-up questions, asked above the composer.
 *
 * Clarifications used to render as chips inside the assistant bubble, and
 * picking one called sendMessage(option) — an ordinary chat message. That is
 * what made a follow-up count as a second run in observability and a second
 * charge against the message allowance: the UI and the accounting were the
 * same bug. Answers submitted from here go back as answers to the question
 * that is already open, so one enquiry stays one enquiry.
 *
 * It also lifts the ceiling on what can be asked. Chips could only ever offer
 * "pick exactly one of these"; this renders single-select, multi-select and
 * free text, and walks through up to three questions, so the analyst can ask
 * the thing it actually needs rather than the nearest single-choice
 * approximation.
 *
 * Above the composer rather than in the thread, because a question you must
 * answer is not history — it scrolls away with the conversation and stops
 * being findable exactly when it matters.
 *
 * Keyboard is first-class: ↑↓ move, Enter selects, ⌘/Ctrl+Enter submits,
 * Esc skips. Anyone answering three questions with a mouse will resent it.
 */

const isAnswered = (q, v) => {
  if (q.type === 'multi_select') return Array.isArray(v) && v.length > 0;
  if (q.type === 'text') return typeof v === 'string' && v.trim().length > 0;
  return v != null && v !== '';
};

export default function ClarifyCard({
  questions = [],
  onSubmit,
  onSkip,
  // Closing (X, Escape) only hides the card. Skip answers the question.
  onDismiss,
  submitting = false,
  error = null,
}) {
  const [index, setIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [cursor, setCursor] = useState(0);
  const rootRef = useRef(null);
  const textRef = useRef(null);

  const total = questions.length;
  const q = questions[index];
  const value = q ? answers[q.id] : undefined;

  // Focus the card on mount so the keyboard shortcuts work without a click.
  useEffect(() => { rootRef.current?.focus(); }, []);
  useEffect(() => { setCursor(0); if (q?.type === 'text') textRef.current?.focus(); }, [index, q?.type]);

  const setAnswer = useCallback((id, v) => setAnswers((prev) => ({ ...prev, [id]: v })), []);

  const toggle = useCallback((opt) => {
    if (!q) return;
    if (q.type === 'multi_select') {
      const cur = Array.isArray(value) ? value : [];
      setAnswer(q.id, cur.includes(opt.value) ? cur.filter((v) => v !== opt.value) : [...cur, opt.value]);
    } else {
      // Re-picking the selected option clears it — otherwise a single-select
      // answered by mistake can never be un-answered on an optional question.
      setAnswer(q.id, value === opt.value ? undefined : opt.value);
    }
  }, [q, value, setAnswer]);

  const answeredCount = useMemo(
    () => questions.filter((qq) => isAnswered(qq, answers[qq.id])).length,
    [questions, answers],
  );

  // Submit sends every answer at once, not one per question: the analyst
  // re-plans from the whole set, and a half-submitted clarification would
  // resume the run on partial information.
  const submit = useCallback(() => {
    if (submitting) return;
    const cleaned = {};
    for (const qq of questions) {
      const v = answers[qq.id];
      if (isAnswered(qq, v)) cleaned[qq.id] = qq.type === 'text' ? String(v).trim() : v;
    }
    onSubmit?.(cleaned);
  }, [answers, questions, onSubmit, submitting]);

  const onKeyDown = useCallback((e) => {
    if (e.key === 'Escape') { e.preventDefault(); (onDismiss || onSkip)?.(); return; }
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); submit(); return; }
    if (!q || q.type === 'text') return; // let the textarea own its keys
    const opts = q.options || [];
    if (e.key === 'ArrowDown') { e.preventDefault(); setCursor((c) => Math.min(opts.length - 1, c + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setCursor((c) => Math.max(0, c - 1)); }
    else if (e.key === 'Enter') { e.preventDefault(); if (opts[cursor]) toggle(opts[cursor]); }
  }, [q, cursor, toggle, submit, onSkip, onDismiss]);

  if (!total || !q) return null;

  const selectedLabel = q.type === 'multi_select'
    ? `${Array.isArray(value) ? value.length : 0} selected`
    : `${answeredCount} of ${total} answered`;

  return (
    <div
      ref={rootRef}
      tabIndex={-1}
      role="group"
      aria-label="Follow-up questions"
      onKeyDown={onKeyDown}
      className="mb-3 rounded-xl border bg-surface shadow-token-lg outline-none"
      style={{ borderColor: 'var(--border-default)' }}
    >
      <div className="flex items-start justify-between gap-3 px-4 pt-3.5 pb-2">
        <p className="text-[14px] font-medium text-[color:var(--text-primary)]">{q.prompt}</p>
        <div className="flex flex-none items-center gap-1 text-[11px] text-[color:var(--text-muted)]">
          {total > 1 && (
            <>
              <button
                type="button" aria-label="Previous question" disabled={index === 0}
                onClick={() => setIndex((i) => Math.max(0, i - 1))}
                className="rounded p-0.5 transition-colors hover:bg-surface-hover disabled:opacity-30"
              ><ChevronLeft size={14} /></button>
              <span className="tabular-nums">{index + 1} of {total}</span>
              <button
                type="button" aria-label="Next question" disabled={index === total - 1}
                onClick={() => setIndex((i) => Math.min(total - 1, i + 1))}
                className="rounded p-0.5 transition-colors hover:bg-surface-hover disabled:opacity-30"
              ><ChevronRight size={14} /></button>
            </>
          )}
          <button
            type="button" aria-label="Dismiss" onClick={() => (onDismiss || onSkip)?.()}
            className="ml-1 rounded p-0.5 transition-colors hover:bg-surface-hover"
          ><X size={14} /></button>
        </div>
      </div>

      {q.type === 'text' ? (
        <div className="px-4 pb-1">
          <textarea
            ref={textRef}
            rows={2}
            value={typeof value === 'string' ? value : ''}
            onChange={(e) => setAnswer(q.id, e.target.value)}
            placeholder="Type your answer…"
            className="w-full resize-none rounded-lg border bg-transparent px-3 py-2 text-[13px] text-[color:var(--text-primary)] outline-none placeholder:text-[color:var(--text-muted)]"
            style={{ borderColor: 'var(--border-default)' }}
          />
        </div>
      ) : (
        <ul className="pb-1">
          {(q.options || []).map((opt, i) => {
            const picked = q.type === 'multi_select'
              ? Array.isArray(value) && value.includes(opt.value)
              : value === opt.value;
            return (
              <li key={opt.value}>
                <button
                  type="button"
                  onClick={() => { setCursor(i); toggle(opt); }}
                  aria-pressed={picked}
                  className="flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors"
                  style={{ backgroundColor: i === cursor ? 'var(--surface-hover)' : 'transparent' }}
                >
                  <span
                    className="flex h-4 w-4 flex-none items-center justify-center border"
                    style={{
                      borderRadius: q.type === 'multi_select' ? 4 : 999,
                      borderColor: picked ? 'var(--brand-primary)' : 'var(--border-default)',
                      backgroundColor: picked ? 'var(--brand-primary)' : 'transparent',
                    }}
                  >
                    {picked && <Check size={11} strokeWidth={3} color="var(--text-on-brand, #fff)" />}
                  </span>
                  <span className="text-[13px] text-[color:var(--text-primary)]">{opt.label}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {error && (
        <p className="px-4 pb-1 text-[12px]" style={{ color: 'var(--feedback-error)' }}>{error}</p>
      )}

      <div
        className="flex items-center justify-between border-t px-4 py-2.5"
        style={{ borderColor: 'var(--border-subtle)' }}
      >
        <span className="text-[12px] text-[color:var(--text-muted)]">{selectedLabel}</span>
        <div className="flex items-center gap-2">
          <button
            type="button" onClick={() => onSkip?.()} disabled={submitting}
            className="rounded-lg border px-3 py-1.5 text-[12px] font-medium transition-colors hover:bg-surface-hover disabled:opacity-50"
            style={{ borderColor: 'var(--border-default)', color: 'var(--text-secondary)' }}
          >Skip</button>
          <button
            type="button" onClick={submit} disabled={submitting}
            aria-label="Submit answers"
            className="flex h-8 w-8 items-center justify-center rounded-lg transition-opacity hover:opacity-90 disabled:opacity-40"
            style={{ backgroundColor: 'var(--brand-primary)', color: 'var(--text-on-brand, #fff)' }}
          ><ArrowRight size={15} /></button>
        </div>
      </div>

      <div className="px-4 pb-2.5 text-[11px] text-[color:var(--text-muted)]">
        ↑↓ to navigate · Enter to select · ⌘Enter to submit · Esc to close
      </div>
    </div>
  );
}
